import { Box, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useRouterState } from "@tanstack/react-router";

import { RouteLink, StyledAnchor } from "@/components/common/styles";
import { marketingDestination } from "@/components/marketing/marketingLinks";
import {
  ACCENT,
  SITE_STEPS,
  marketingPalette,
} from "@/components/marketing/tokens";
import {
  FOOTER_BLOG_ROUTE,
  FOOTER_COLUMNS,
  type FooterColumn,
  type FooterLink as FooterLinkItem,
} from "./footerContent";

const NAV_LABEL = "Footer";

/** A row dims to half until pointed at, fading on opacity alone so the ink stays white. */
const LINK = (theme: Theme) => ({
  py: 1.5,
  fontSize: 14,
  fontWeight: 300,
  lineHeight: "23px",
  color: "common.white",
  opacity: 0.5,
  transition: "opacity 0.2s",
  "&:hover": { opacity: 1 },
  [theme.breakpoints.down(SITE_STEPS.wide)]: { fontWeight: 400 },
});

/** The row that closes a column reads at full strength and underlines instead. */
const VIEW_ALL = {
  opacity: 1,
  "&:hover": { textDecoration: "underline" },
};

/** The row for the page you are already on. `pointer-events` is the published way of parking it; `aria-current` is ours. */
const CURRENT = {
  opacity: 1,
  color: marketingPalette.inkSubtle,
  pointerEvents: "none",
};

/** A parked view-all row takes the accent, not the grey `CURRENT` sets, so this follows it and puts the colour back. */
const CURRENT_VIEW_ALL = { color: ACCENT };

/** `.footer_link-title`. */
export const COLUMN_TITLE = {
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "19.5px",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "common.white",
};

const LIST = {
  display: "flex",
  flexDirection: "column",
  listStyle: "none",
  m: 0,
  mb: "10px",
  p: 0,
};

/**
 * Where a footer item is a route of this app: the blog listing, and the
 * Company column's legal pages, which the app's own dynamic route serves rather
 * than a trip to the CMS. Null for every other destination.
 */
function routeTo(href: string) {
  if (href === FOOTER_BLOG_ROUTE) return FOOTER_BLOG_ROUTE;
  const destination = marketingDestination(href);
  return destination.internal ? destination.to : null;
}

export function FooterLink({
  item,
  current,
}: {
  item: FooterLinkItem;
  current: boolean;
}) {
  const viewAll = item.viewAll === true;
  const sx = [
    LINK,
    viewAll && VIEW_ALL,
    current && CURRENT,
    current && viewAll && CURRENT_VIEW_ALL,
  ];
  const to = routeTo(item.href);

  if (to !== null) {
    return (
      <RouteLink
        to={to}
        activeOptions={{ exact: true }}
        aria-current={current ? "page" : undefined}
        sx={sx}
      >
        {item.label}
      </RouteLink>
    );
  }

  return (
    <StyledAnchor
      href={item.href}
      target={item.newTab === true ? "_blank" : undefined}
      rel={item.newTab === true ? "noopener" : undefined}
      sx={sx}
    >
      {item.label}
    </StyledAnchor>
  );
}

function LinkColumn({ column, here }: { column: FooterColumn; here: string }) {
  const titleId = `footer-${column.title.replace(/\W+/g, "-").toLowerCase()}`;
  return (
    <Box
      sx={(theme) => ({
        [theme.breakpoints.down(SITE_STEPS.wide)]: { mx: 2 },
        [theme.breakpoints.down(SITE_STEPS.card)]: {
          width: "100%",
          mx: 0,
          mb: 4,
        },
        [theme.breakpoints.down(SITE_STEPS.tiny)]: { mb: 0 },
      })}
    >
      {/* `footer_link-title` — a label, not a heading: as headings these five
          titles would add five entries to every page's outline. */}
      <Typography component="div" id={titleId} sx={COLUMN_TITLE}>
        {column.title}
      </Typography>
      {column.lists.map((list) => (
        <Box
          component="ul"
          key={list[0].href}
          aria-labelledby={titleId}
          sx={LIST}
        >
          {list.map((item) => (
            <Box component="li" key={item.href} sx={{ my: 1.5 }}>
              <FooterLink item={item} current={item.href === here} />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/**
 * The five link columns: a five-track grid below the wide step and a two-track
 * one below the card step. The published site never turns them into
 * disclosures, so nothing here opens or closes. The `nav` landmark is ours.
 */
export default function FooterNav() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const here = pathname.replace(/\/$/, "");

  return (
    <Box
      component="nav"
      aria-label={NAV_LABEL}
      sx={(theme) => ({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        columnGap: "28.8px",
        width: "100%",
        maxWidth: 752,
        [theme.breakpoints.down(SITE_STEPS.wide)]: {
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
          justifyContent: "space-around",
          rowGap: 3,
          columnGap: 2,
        },
        [theme.breakpoints.down(SITE_STEPS.card)]: {
          gridTemplateColumns: "1fr 1fr",
          justifyContent: "flex-start",
          // Inert once the row is a grid, and left behind by the published rule.
          flexWrap: "wrap",
        },
        [theme.breakpoints.down(SITE_STEPS.tiny)]: { columnGap: 3 },
      })}
    >
      {FOOTER_COLUMNS.map((column) => (
        <LinkColumn key={column.title} column={column} here={here} />
      ))}
    </Box>
  );
}
